/**
 * WhatsApp pairing RPC schemas (CLA-98 / CLA-707).
 *
 * The pairing lifecycle is driven from TWO rails — the customer connections
 * group (`AuthMiddleware`) and the admin installation-connections group
 * (`AdminMiddleware`) — so its request/response schemas and wire error live in
 * this neutral module that neither RPC group imports the other through. This
 * breaks the schema-initialization cycle that would arise if the admin group
 * imported these from the customer `connections.rpc` module (which already
 * imports `ConnectStatusResponse` from the admin group).
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

/**
 * Wire error for the WhatsApp pairing lifecycle — the api-owned projection of
 * the cluster entity's `WhatsappPairingError`. Defined natively here (not
 * imported from `@fm/cluster`) so the web-facing RPC surface never drags the
 * cluster package into the browser's type graph; the server maps the entity's
 * error onto this at the handler boundary. `phase` narrows where the failure
 * happened; `message` is human-readable and MUST NEVER embed credential
 * material.
 *
 * @since 1.0.0
 */
export class WhatsappPairingRpcError extends Schema.TaggedError<WhatsappPairingRpcError>()("WhatsappPairingError", {
  phase: Schema.Literal("boot", "timeout", "protocol", "serialization", "cancelled"),
  message: Schema.String,
}) {}

/**
 * Address a WhatsApp pairing round-trip. The `installationId` names the
 * `agent_installations` row that keys the `WhatsappPairingEntity`. The customer
 * rail verifies the acting person owns it (through their memberships) before
 * touching the entity; the admin rail addresses it directly by id under the
 * global admin gate.
 *
 * @since 1.0.0
 */
export const WhatsappPairingInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  /**
   * Full international phone number for the phone-only linking path. When
   * present, `whatsappStartPairing` returns an 8-char pairing code (via the
   * poll's `pairingCode`) the customer types into WhatsApp instead of
   * scanning a QR. Absent → QR pairing. Only `whatsappStartPairing` reads
   * it; poll/cancel ignore it.
   */
  phoneNumber: Schema.optional(Schema.String),
});
export type WhatsappPairingInput = Schema.Schema.Type<typeof WhatsappPairingInput>;

/**
 * Acknowledgment that a pairing session is live: the entity's correlation id
 * for this attempt (idempotent — a live session returns its existing id).
 *
 * @since 1.0.0
 */
export const WhatsappStartPairingResponse = Schema.Struct({
  correlationId: Schema.String,
});
export type WhatsappStartPairingResponse = Schema.Schema.Type<typeof WhatsappStartPairingResponse>;

/**
 * The pollable pairing state as it crosses to the browser — the entity's
 * pairing snapshot with the single-delivery `session` blob OMITTED. The
 * session (Baileys creds) is persisted server-side on the `connected` poll and
 * MUST never reach the wire; the field's absence from this schema is the
 * structural guarantee of that. Field set mirrors the cluster entity's
 * `PairingState` minus `session`.
 *
 * @since 1.0.0
 */
export const WhatsappPollPairingResponse = Schema.Struct({
  status: Schema.Literal("idle", "pairing", "connected", "timeout", "error", "cancelled"),
  /** base64 data URL of the CURRENT QR (rotates ~20s). */
  qrPng: Schema.optional(Schema.String),
  qrIssuedAt: Schema.optional(Schema.DateTimeUtc),
  /**
   * 8-char phone-number pairing code, present while pairing when the caller
   * supplied a phone number. NOT a secret (the customer must read it), so —
   * unlike `session` — it crosses to the wire on every poll.
   */
  pairingCode: Schema.optional(Schema.String),
  /** E.164 phone number, present when connected. */
  phoneNumber: Schema.optional(Schema.String),
  deviceId: Schema.optional(Schema.String),
  /** present when status is `error`. */
  message: Schema.optional(Schema.String),
});
export type WhatsappPollPairingResponse = Schema.Schema.Type<typeof WhatsappPollPairingResponse>;

/**
 * Acknowledgment that a pairing session was cancelled (socket closed, logged
 * out).
 *
 * @since 1.0.0
 */
export const WhatsappCancelPairingResponse = Schema.Struct({
  success: Schema.Boolean,
});
export type WhatsappCancelPairingResponse = Schema.Schema.Type<typeof WhatsappCancelPairingResponse>;
